// Dependencies
"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get CreateGoalArguments () {
        return CreateGoalArguments;
    },
    get GoalAction () {
        return GoalAction;
    },
    get GoalCategoryModel () {
        return GoalCategoryModel;
    },
    get GoalChecklistItemModel () {
        return GoalChecklistItemModel;
    },
    get GoalColorModel () {
        return GoalColorModel;
    },
    get GoalDetailsModel () {
        return GoalDetailsModel;
    },
    get GoalHistoryModel () {
        return GoalHistoryModel;
    },
    get GoalModel () {
        return GoalModel;
    },
    get GoalStatus () {
        return GoalStatus;
    },
    get GoalTransparency () {
        return GoalTransparency;
    },
    get UpdateGoalArguments () {
        return UpdateGoalArguments;
    },
    get UpdateGoalProgressArguments () {
        return UpdateGoalProgressArguments;
    },
    get createGoalHistoryEntryFields () {
        return createGoalHistoryEntryFields;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const _user = require("./user");
const GoalTransparency = _zod.z.enum([
    "private",
    "public"
]);
const GoalAction = _zod.z.enum([
    "add-category",
    "add-checklist",
    "add-sharer",
    "check-checklist",
    "create",
    "delete-checklist",
    "remove-category",
    "remove-sharer",
    "uncheck-checklist",
    "update"
]);
const GoalStatus = _zod.z.enum([
    "active",
    "archived",
    "deleted",
    "done"
]);
const GoalModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * Flag to indicate if the goal is a top level goal.
	 * If true, the goal should appear at the top level of the goal tree.
	 **/ is_top: _zod.z.boolean(),
    /**
	 * The date the goal will start.
	 **/ start_date: _zod.z.date(),
    /**
	 * A list of categories associated with the goal. These categories are custom
	 * to each company and have their own documents in the database under strategy_category.
	 **/ categories: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    /**
	 * The starting value of the goal.
	 **/ initial_value: _zod.z.number(),
    /**
	 * Represents whether the goal is public or private.
	 * If public, the goal will be visible to all employees.
	 * If private, the goal will only be visible to the owner and sharers.
	 **/ transparency: GoalTransparency,
    /**
	 * A list of the users the goal has been shared with. If the list is empty,
	 * then we assume the goal has not been shared with anyone.
	 **/ sharers: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    /**
	 * The value of the goal desired by the end of the goal period.
	 **/ target: _zod.z.number(),
    /**
	 * The name of the goal.
	 **/ name: _zod.z.string(),
    /**
	 * The description of the goal.
	 **/ description: _zod.z.string(),
    /**
	 * The user who created the goal and controls it.
	 */ owner: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * A list of the users who have seen the goal.
	 **/ seen_by: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    /**
	 * The goal's current value. Where the goal is in its progress.
	 **/ value: _zod.z.number(),
    /**
	 * Weight of the goal. Used to calculate the goal's priority.
	 */ goal_weight: _zod.z.number().nullable(),
    /**
	 * The user who is working towards the goal. Could be the owner of the goal or someone else.
	 **/ assignee: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The company the goal and users associated with the goal belong to.
	 **/ company: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The date the goal was last updated.
	 **/ last_update: _zod.z.date(),
    /**
	 * Units the goal is measured in. Common examples are $, %, and #, but can be anything the user defines.
	 **/ units: _zod.z.string(),
    /**
	 * The status of the goal. Can be active, archived, deleted, or done.
	 **/ status: GoalStatus,
    /**
	 * Represents another goal's id that this goal is aligned with.
	 **/ alignment: _zod.z.instanceof(_bson.ObjectId).optional().nullable(),
    /**
	 * Flag to indicate the goal has been deleted.
	 **/ deleted: _zod.z.boolean().optional(),
    /**
	 * The due date of the goal.
	 **/ end_date: _zod.z.date().optional().nullable(),
    /**
	 * Represent the color document associated with the goal. Colors are custom to each company and have their own
	 * documents under strategy_color.
	 */ color: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * The ObjectId of an async job that the goal is associated with. Example: "create-goals" async job.
	 **/ job_id: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * Legacy field that was used to indicate if the goal was migrated from the old system.
	 * We no longer use this field.
	 */ was_migrated: _zod.z.boolean().optional(),
    /**
	 * Used to represent the goal's alignment tree. Ancestors of aligned goals are added alongside aligned goals to each goal that assigns alignment.
	 */ ancestors: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
    /**
	 * Flag to indicate if the goal's checklist updates the goal's progress.
	 */ checklist_updates_goal_progress: _zod.z.boolean().optional()
});
const GoalDetailsModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    is_top: _zod.z.boolean().optional(),
    start_date: _zod.z.date(),
    categories: _zod.z.array(_zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        name: _zod.z.string()
    })).nullish(),
    initial_value: _zod.z.number().nullable().optional(),
    transparency: GoalTransparency,
    sharers: _zod.z.array(_user.SkinnyEmployee).optional(),
    target: _zod.z.number(),
    name: _zod.z.string(),
    description: _zod.z.string(),
    owner: _zod.z.instanceof(_bson.ObjectId).optional(),
    seen_by: _zod.z.array(_zod.z.string()).optional(),
    value: _zod.z.number(),
    assignee: _zod.z.array(_user.SkinnyEmployee),
    company: _zod.z.instanceof(_bson.ObjectId).optional(),
    last_update: _zod.z.date().optional(),
    units: _zod.z.string(),
    status: GoalStatus,
    alignment: _zod.z.instanceof(_bson.ObjectId).nullable().optional(),
    deleted: _zod.z.boolean().optional(),
    end_date: _zod.z.date().nullable().optional(),
    outlook: _zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        name: _zod.z.string(),
        color: _zod.z.string()
    }).nullable().optional(),
    goal_weight: _zod.z.number().nullable().optional(),
    checklist: _zod.z.array(_zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        text: _zod.z.string(),
        checked: _zod.z.boolean()
    })).nullable().optional(),
    checklist_updates_goal_progress: _zod.z.boolean().optional()
});
const GoalCategoryModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The ObjectId of the company the category belongs to.
	 **/ company: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * The name or label of the category.
	 **/ name: _zod.z.string(),
    /**
	 * Flag to indicate if the category has been deleted.
	 **/ deleted: _zod.z.boolean().optional(),
    /**
	 * If the category has been deleted, the ObjectId of the user who deleted it.
	 **/ deleter: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * If the category has been deleted, the date it was deleted.
	 **/ deletion_date: _zod.z.date().optional()
});
const GoalChecklistItemModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The ObjectId of the goal the checklist item belongs to.
	 **/ strategy: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The name of the checklist item.
	 **/ text: _zod.z.string(),
    /**
	 * Flag to indicate if the checklist item has been completed.
	 **/ checked: _zod.z.boolean(),
    /**
	 * The ObjectId of the company the checklist item was created under.
	 **/ company: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * Flag to indicate if the checklist item has been deleted.
	 **/ deleted: _zod.z.boolean().optional(),
    /**
	 * Legacy field that was used to indicate if the checklist item was migrated from the old system.
	 * We no longer use this field.
	 **/ was_migrated: _zod.z.boolean().optional()
});
const GoalColorModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * Hex code of the color.
	 **/ color: _zod.z.string(),
    /**
	 * The ObjectId of the company the color belongs to.
	 **/ company: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * The name of the color. Can be anything the user defines.
	 **/ name: _zod.z.string(),
    /**
	 * The order of the color. Used to sort the colors in the UI.
	 **/ order: _zod.z.number().optional(),
    /**
	 * Flag to indicate if the color has been deleted.
	 **/ deleted: _zod.z.boolean().optional(),
    /**
	 * If the color has been deleted, the ObjectId of the user who deleted it.
	 **/ deleter: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * If the color has been deleted, the date it was deleted.
	 **/ deletion_date: _zod.z.date().optional()
});
const GoalHistoryModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The action or change that was made to the goal.
	 **/ action: GoalAction,
    /**
	 * The ObjectId of user (or API Key) who made the change.
	 **/ actor: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * The date the change was made.
	 **/ as_of: _zod.z.date(),
    /**
	 * If a category was changed, the ObjectId of that category.
	 **/ category: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * An object where the keys are the fields that were changed and the values are the new values.
	 * Example: { name: "New Name" }
	 **/ change: _zod.z.object({}).optional(),
    /**
	 * If a checklist item was changed, the ObjectId of that checklist item.
	 **/ checklist_item: _zod.z.instanceof(_bson.ObjectId).optional(),
    /**
	 * The ObjectId of the company the goal belongs to.
	 **/ company: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * If the entity was deleted, the date it was deleted and the user who deleted it.
	 **/ deleted: _zod.z.object({
        date: _zod.z.date(),
        user: _zod.z.instanceof(_bson.ObjectId)
    }).optional(),
    /**
	 * Fields that need to be joined to the history document.
	 **/ join_fields: _zod.z.object({}).optional(),
    /**
	 * Any message that was added to explain the change.
	 **/ message: _zod.z.string().optional(),
    /**
	 * If a checklist item was changed, the original checklist item before the change.
	 **/ original_checklist_item: GoalChecklistItemModel.optional(),
    /**
	 * If something on the goal was changed, the original goal before the change.
	 **/ original_strategy: _zod.z.union([
        GoalModel.partial(),
        GoalDetailsModel.merge(_zod.z.object({
            assignee: _zod.z.object({
                _id: _zod.z.string(),
                name: _zod.z.string()
            })
        })).partial()
    ]).optional(),
    /**
	 * If something on the goal was changed, this is the new goal after the change.
	 **/ strategy: _zod.z.instanceof(_bson.ObjectId),
    /**
	 * Legacy field that was used to indicate if the history item was migrated from the old system.
	 * We no longer use this field.
	 **/ was_migrated: _zod.z.boolean().optional()
});
const createGoalHistoryEntryFields = _zod.z.object({
    companyId: _zod.z.instanceof(_bson.ObjectId),
    goalId: _zod.z.instanceof(_bson.ObjectId),
    change: _zod.z.object({}).optional(),
    asOfDate: _zod.z.date(),
    message: _zod.z.string().optional(),
    action: GoalAction,
    actorId: _zod.z.instanceof(_bson.ObjectId),
    originalGoal: _zod.z.object({}).optional()
});
const UpdateGoalProgressArguments = _zod.z.object({
    companyId: _zod.z.instanceof(_bson.ObjectId),
    goalId: _zod.z.instanceof(_bson.ObjectId),
    actorId: _zod.z.instanceof(_bson.ObjectId),
    progressUpdate: _zod.z.object({
        value: _zod.z.number(),
        asOfDate: _zod.z.date(),
        message: _zod.z.string().optional(),
        outlook: _zod.z.instanceof(_bson.ObjectId).optional()
    }),
    checklist_updates_goal_progress: _zod.z.boolean().optional()
}).strict();
const UpdateGoalArguments = _zod.z.object({
    companyId: _zod.z.instanceof(_bson.ObjectId),
    goalId: _zod.z.instanceof(_bson.ObjectId),
    actorId: _zod.z.instanceof(_bson.ObjectId),
    update: _zod.z.object({
        name: _zod.z.string().optional(),
        description: _zod.z.string().optional(),
        target: _zod.z.number().optional(),
        units: _zod.z.string().optional(),
        startDate: _zod.z.date().optional(),
        endDate: _zod.z.date().optional().nullable(),
        categories: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
        goalWeight: _zod.z.number().optional().nullable(),
        transparency: GoalTransparency.optional(),
        isTopLevel: _zod.z.boolean().optional(),
        alignment: _zod.z.instanceof(_bson.ObjectId).optional().nullable(),
        ancestors: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
        checklist_updates_goal_progress: _zod.z.boolean().optional()
    })
}).strict();
const CreateGoalArguments = _zod.z.object({
    companyId: _zod.z.instanceof(_bson.ObjectId),
    name: _zod.z.string(),
    assignee: _zod.z.instanceof(_bson.ObjectId),
    owner: _zod.z.instanceof(_bson.ObjectId),
    transparency: GoalTransparency,
    target: _zod.z.number(),
    units: _zod.z.string(),
    startDate: _zod.z.date(),
    endDate: _zod.z.date().optional(),
    description: _zod.z.string().optional(),
    categories: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
    goalWeight: _zod.z.number().optional(),
    alignment: _zod.z.instanceof(_bson.ObjectId).optional(),
    ancestors: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)).optional(),
    isTopLevel: _zod.z.boolean().optional(),
    checklist_updates_goal_progress: _zod.z.boolean().optional()
}).strict();
